import type { MediaRef } from "@velizhanin-dev/bot-scenario"
import type { AudienceFilter, AudienceVariable } from "@/entities/audience"
import type { MediaKind, MediaSource } from "@/entities/bot-builder"
import { apiClient } from "@/shared/api/client"

export type BroadcastStatus = "DRAFT" | "SCHEDULED" | "SENDING" | "DONE" | "CANCELLED"

export interface BroadcastStats {
	total: number
	pending: number
	sent: number
	failed: number
	blocked: number
	/** Люди без Telegram ID: ищем, отправим, как только найдём. */
	waitingId: number
	/** Telegram ID не нашёлся: сообщение не ушло. */
	noId: number
}

export interface BroadcastDraftButton {
	id: string
	kind: "url" | "flow"
	label: string
	url?: string
	flowId?: string
}

export interface BroadcastDraftContent {
	text: string
	media?: MediaRef
	/** Альбом: 2–10 фото и видео одним сообщением; вместе с media не бывает. */
	gallery?: MediaRef[]
	buttons: BroadcastDraftButton[]
	silent?: boolean
	protect?: boolean
	noLinkPreview?: boolean
}

export interface BroadcastSummary {
	id: string
	botId: string
	name: string
	status: BroadcastStatus
	scheduledAt: string | null
	startedAt: string | null
	finishedAt: string | null
	createdAt: string
	updatedAt: string
	stats: BroadcastStats
}

export interface BroadcastContext {
	botUsername: string
	/** Есть опубликованная версия сценария: кнопки могут запускать потоки. */
	published: boolean
	flows: { id: string; name: string; kind: "MAIN" | "FALLBACK" | "CUSTOM" }[]
	variables: AudienceVariable[]
	/** Группы (метки) черновика: рассылка по группе. */
	tags: { id: string; name: string; archivedAt?: string | null }[]
}

export interface Broadcast extends BroadcastSummary {
	content: BroadcastDraftContent
	filter: AudienceFilter
	context: BroadcastContext
	failures: {
		id: string
		error: string | null
		telegramUserId: string
		profile: { firstName?: string; lastName?: string | null; username?: string | null } | null
	}[]
}

export interface UpdateBroadcastBody {
	name?: string
	content?: BroadcastDraftContent
	filter?: AudienceFilter
}

export const broadcastKeys = {
	list: (botId: string) => ["bot-builder", "broadcasts", botId] as const,
	detail: (botId: string, id: string) => ["bot-builder", "broadcasts", botId, "item", id] as const,
	count: (botId: string, filter: AudienceFilter) =>
		["bot-builder", "broadcasts", botId, "count", filter] as const,
}

const path = (botId: string, rest = "") => `/admin/custom-bots/${botId}/broadcasts${rest}`

const cleanFilter = (filter: AudienceFilter) =>
	Object.fromEntries(
		Object.entries(filter).filter(([, value]) => value !== undefined && value !== ""),
	)

export const listBroadcasts = async (botId: string) =>
	(await apiClient.get<BroadcastSummary[]>(path(botId))).data

export const getBroadcast = async (botId: string, id: string) =>
	(await apiClient.get<Broadcast>(path(botId, `/${id}`))).data

export const createBroadcast = async (botId: string, body: { name: string }) =>
	(await apiClient.post<Broadcast>(path(botId), body)).data

export const updateBroadcast = async (botId: string, id: string, body: UpdateBroadcastBody) =>
	(
		await apiClient.patch<Broadcast>(path(botId, `/${id}`), {
			...body,
			...(body.filter ? { filter: cleanFilter(body.filter) } : {}),
		})
	).data

export const sendBroadcast = async (botId: string, id: string, body: { scheduledAt?: string }) =>
	(await apiClient.post<Broadcast>(path(botId, `/${id}/send`), body)).data

/** Проверка себе в Telegram: уходит через бота рассылки, сам черновик не трогает. */
export const testBroadcast = async (botId: string, id: string) =>
	(await apiClient.post<{ botUsername: string }>(path(botId, `/${id}/test`))).data

export const cancelBroadcast = async (botId: string, id: string) =>
	(await apiClient.post<Broadcast>(path(botId, `/${id}/cancel`))).data

export const duplicateBroadcast = async (botId: string, id: string) =>
	(await apiClient.post<Broadcast>(path(botId, `/${id}/duplicate`))).data

export const deleteBroadcast = async (botId: string, id: string) => {
	await apiClient.delete(path(botId, `/${id}`))
}

export interface RecipientCount {
	count: number
	/** Перенесённые без Telegram ID: сообщение уйдёт, как только найдём ID. */
	withoutId: number
}

/** Сколько человек получит рассылку: с Telegram ID и ещё без него. */
export const totalOf = (data: RecipientCount | undefined) => data && data.count + data.withoutId

export const countBroadcastRecipients = async (botId: string, filter: AudienceFilter) =>
	(
		await apiClient.post<RecipientCount>(
			path(botId, "/count"),
			cleanFilter(filter),
		)
	).data

export const presignBroadcastMedia = async (
	botId: string,
	body: { kind: MediaKind; fileName: string; mime: string; size: number },
) =>
	(
		await apiClient.post<{ uploadUrl: string; expiresIn: number; media: MediaRef }>(
			path(botId, "/media/presign"),
			body,
		)
	).data

export const getBroadcastMediaUrl = async (botId: string, s3Key: string) =>
	(
		await apiClient.get<{ url: string; expiresIn: number }>(path(botId, "/media/url"), {
			params: { s3Key },
		})
	).data

export const broadcastMediaSource = (botId: string): MediaSource => ({
	cacheKey: ["bot-builder", "broadcast-media", botId],
	presign: (body) => presignBroadcastMedia(botId, body),
	previewUrl: (s3Key) => getBroadcastMediaUrl(botId, s3Key),
})
